import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { useHistory } from "react-router-dom";
import { Paginator } from "primereact/paginator";
import { handleGetRequest } from "../../services/GetTemplate";
import { Tooltip } from "react-tippy";
import { toast } from "react-toastify";
import { foldInvoiceCharges } from "../../services/additionalInvoiceService";
import styles from "../billing/BillingReports.module.scss";

function BillingReports() {
    const [selectedRow, setselectedRow] = useState([]);
    const [allInvoices, setAllInvoices] = useState([]);
    const [userId, setuserId] = useState("");
    const [loading, setLoading] = useState(false);

    const [brands, setBrands] = useState([]);
    const [filteredBrands, setFilteredBrands] = useState([]);
    const [groupedData, setGroupedData] = useState([]);
    const [paginatedGroups, setPaginatedGroups] = useState([]);
    const [globalSearch, setGlobalSearch] = useState("");
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    const [paginatedInvoices, setPaginatedInvoices] = useState([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [groupsPerPage] = useState(5); // paid tab: 5 payment-date groups per page
    const [invoicesPerPage] = useState(12); // unpaid tab: invoices aren't grouped, so paginate individually

    // Client invoices only ever come back reviewed (see GetClientInvoices),
    // so there's no "Pending Review" bucket here - just Unpaid vs Paid.
    const [activeTab, setActiveTab] = useState("unpaid");

    const history = useHistory();

    const [advancedFilters, setAdvancedFilters] = useState({
        invoiceId: "",
        invoiceNo: "",
        clientName: "",
        customerType: "",
        weight: "",
        invoiceAmount: "",
        shipmentNumber: "",
        descriptions: "",
        invoiceType: "",
        date: "",
        datePaid: "",
        paidAmount: "",
    });

    useEffect(() => {
        const storedUserId = localStorage.getItem("userId");
        setuserId(storedUserId);
    }, []);

    // Check if filters are active
    const isFilterActive = () => {
        return globalSearch !== "" || Object.values(advancedFilters).some((v) => v !== "");
    };

    // Group data by payment date for paid invoices
    const groupByPaymentDate = (data) => {
        const groups = {};
        data.forEach((item) => {
            const paymentDate = item.datePaid || "No Payment Date";
            if (!groups[paymentDate]) {
                groups[paymentDate] = [];
            }
            groups[paymentDate].push(item);
        });
        return Object.keys(groups)
            .sort((a, b) => new Date(b) - new Date(a))
            .reduce((acc, key) => {
                acc[key] = groups[key];
                return acc;
            }, {});
    };

    // Paginate: paid invoices are grouped by payment date (paginate by group),
    // unpaid invoices aren't grouped at all - paginate the flat list instead.
    useEffect(() => {
        if (activeTab === "paid") {
            const groupEntries = Object.entries(groupedData);
            if (isFilterActive()) {
                setPaginatedGroups(groupEntries);
            } else {
                const start = currentPage * groupsPerPage;
                setPaginatedGroups(groupEntries.slice(start, start + groupsPerPage));
            }
            setPaginatedInvoices([]);
        } else {
            if (isFilterActive()) {
                setPaginatedInvoices(filteredBrands);
            } else {
                const start = currentPage * invoicesPerPage;
                setPaginatedInvoices(filteredBrands.slice(start, start + invoicesPerPage));
            }
            setPaginatedGroups([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedData, filteredBrands, currentPage, groupsPerPage, invoicesPerPage, activeTab, globalSearch, advancedFilters]);

    // Universal search function
    const performGlobalSearch = (data, searchTerm) => {
        if (!searchTerm || searchTerm.trim() === "") {
            return data;
        }

        const term = searchTerm.toLowerCase();
        return data.filter((item) => {
            return (
                item.invoiceId?.toString().toLowerCase().includes(term) ||
                item.invoiceNo?.toString().toLowerCase().includes(term) ||
                item.clientName?.toLowerCase().includes(term) ||
                item.customerType?.toLowerCase().includes(term) ||
                item.shipmentNumber?.toLowerCase().includes(term) ||
                item.descriptions?.toLowerCase().includes(term) ||
                item.invoiceType?.toLowerCase().includes(term) ||
                item.date?.toLowerCase().includes(term) ||
                item.datePaid?.toLowerCase().includes(term) ||
                item.weight?.toString().includes(term) ||
                item.invoiceAmount?.toString().includes(term) ||
                item.paidAmount?.toString().includes(term)
            );
        });
    };

    // Advanced filter function
    const performAdvancedFilter = (data) => {
        return data.filter((item) => {
            return (
                (!advancedFilters.invoiceId || item.invoiceId?.toString().toLowerCase().includes(advancedFilters.invoiceId.toLowerCase())) &&
                (!advancedFilters.invoiceNo || item.invoiceNo?.toString().toLowerCase().includes(advancedFilters.invoiceNo.toLowerCase())) &&
                (!advancedFilters.clientName || item.clientName?.toLowerCase().includes(advancedFilters.clientName.toLowerCase())) &&
                (!advancedFilters.customerType || item.customerType?.toLowerCase().includes(advancedFilters.customerType.toLowerCase())) &&
                (!advancedFilters.weight || item.weight?.toString().includes(advancedFilters.weight)) &&
                (!advancedFilters.invoiceAmount || item.invoiceAmount?.toString().includes(advancedFilters.invoiceAmount)) &&
                (!advancedFilters.shipmentNumber || item.shipmentNumber?.toLowerCase().includes(advancedFilters.shipmentNumber.toLowerCase())) &&
                (!advancedFilters.descriptions || item.descriptions?.toLowerCase().includes(advancedFilters.descriptions.toLowerCase())) &&
                (!advancedFilters.invoiceType || item.invoiceType?.toLowerCase().includes(advancedFilters.invoiceType.toLowerCase())) &&
                (!advancedFilters.date || item.date?.toLowerCase().includes(advancedFilters.date.toLowerCase())) &&
                (!advancedFilters.datePaid || item.datePaid?.toLowerCase().includes(advancedFilters.datePaid.toLowerCase())) &&
                (!advancedFilters.paidAmount || item.paidAmount?.toString().includes(advancedFilters.paidAmount))
            );
        });
    };

    const applyFilters = (data) => {
        let filtered = data;
        filtered = performGlobalSearch(filtered, globalSearch);
        filtered = performAdvancedFilter(filtered);
        return filtered;
    };

    // Split the client's reviewed invoices into the two tabs whenever the
    // underlying data changes.
    useEffect(() => {
        const tabInvoices = allInvoices.filter((item) => (activeTab === "paid" ? item.paymentStatus === "paid" : item.paymentStatus !== "paid"));
        const sorted = [...tabInvoices].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setBrands(sorted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allInvoices, activeTab]);

    useEffect(() => {
        const filtered = applyFilters(brands);
        setFilteredBrands(filtered);

        setGroupedData(activeTab === "paid" ? groupByPaymentDate(filtered) : {});

        setCurrentPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalSearch, advancedFilters, brands]);

    const getInvoices = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const res = await handleGetRequest(`/clientInvoices/${userId}`);
            setAllInvoices(res?.data || []);
        } catch (error) {
            toast.error("Failed to fetch invoices");
            console.error(error);
        }
        setLoading(false);
    };

    useMemo(() => {
        getInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const switchTab = (tab) => {
        setActiveTab(tab);
        setGlobalSearch("");
        setAdvancedFilters({
            invoiceId: "",
            invoiceNo: "",
            clientName: "",
            customerType: "",
            weight: "",
            invoiceAmount: "",
            shipmentNumber: "",
            descriptions: "",
            invoiceType: "",
            date: "",
            datePaid: "",
            paidAmount: "",
        });
        setShowAdvancedSearch(false);
        setCurrentPage(0);
        setselectedRow([]);
    };

    const handleActionButton = (rowdata) => {
        history.push(`/userInvoice?id=${rowdata._id}`);
    };

    const handleClearFilters = () => {
        setGlobalSearch("");
        setAdvancedFilters({
            invoiceId: "",
            invoiceNo: "",
            clientName: "",
            customerType: "",
            weight: "",
            invoiceAmount: "",
            shipmentNumber: "",
            descriptions: "",
            invoiceType: "",
            date: "",
            datePaid: "",
            paidAmount: "",
        });
        toast.info("Filters cleared");
    };

    const handleSelected = () => {
        if (!selectedRow?.length) {
            return toast.error("Please select at least one invoice to pay");
        }

        const hasPaidInvoice = selectedRow.some((item) => item.paymentStatus === "paid");
        if (hasPaidInvoice) {
            return toast.error("You can't select paid invoices");
        }

        if (selectedRow.length === 1) {
            return history.push(`/userInvoice?id=${selectedRow[0]._id}`);
        }

        const aggregated = selectedRow.reduce(
            (acc, item) => {
                foldInvoiceCharges(acc, item);
                acc.TotalAmount += item.TotalAmount;
                acc.discountedAmount += item.discountedAmount;
                acc.loyaltyDiscount += item.loyaltyDiscount;
                acc.splitFee += item.splitFee;
                acc.weight += item.weight;
                acc.descriptions += acc.descriptions ? `,${item.descriptions}` : item.descriptions;
                acc.shipmentNumber += acc.shipmentNumber ? `,${item.shipmentNumber}` : item.shipmentNumber;
                acc.Waybills += acc.Waybills ? `,${item.Waybills}` : item.Waybills;
                acc.invoiceAmount += item.invoiceAmount;
                acc.paidAmount += item.paidAmount ?? 0;
                acc.itemsPrice += item.invoiceCategory === "purchase" ? 0 : (item.itemsPrice ?? 0);
                acc.clientId = item.clientId;
                acc.clientName = item.clientName;
                acc.email = item.email;
                acc.phone = item.phone;
                acc.customerType = item.customerType;
                acc.date = item.date;
                acc.handling = item.handling ?? 0;
                acc.insurance = item.insurance ?? 0;
                acc.notes = acc.notes || "";
                return acc;
            },
            {
                MonthlyFee: 0,
                TotalAmount: 0,
                WebsiteFee: 0,
                dangerousItemFee: 0,
                deliveryFee: 0,
                customDuties: 0,
                declarationFee: 0,
                discountedAmount: 0,
                loyaltyDiscount: 0,
                oversizeFee: 0,
                packagingFee: 0,
                pickupFee: 0,
                splitFee: 0,
                weight: 0,
                packages: [],
                additionalCharges: [],
                purchaseItems: [],
                descriptions: "",
                shipmentNumber: "",
                Waybills: "",
                invoiceAmount: 0,
                paidAmount: 0,
                itemsPrice: 0,
                clientId: "",
                clientName: "",
                email: "",
                phone: "",
                customerType: "",
                date: "",
                handling: 0,
                insurance: 0,
                notes: "",
            },
        );

        const paidInvoices = selectedRow.map((item) => ({
            _id: item._id,
            paidAmount: item.invoiceAmount,
        }));

        localStorage.setItem("Zazioaggregated", JSON.stringify(aggregated));
        localStorage.setItem("Zaziopaid", JSON.stringify(paidInvoices));
        localStorage.setItem("PaymentUser", "user");
        history.push("/bulkInvoicesUser");
    };

    const isInvoiceSelected = (invoice) => {
        return selectedRow.some((item) => item._id === invoice._id);
    };

    const toggleInvoiceSelection = (invoice, e) => {
        e.stopPropagation();
        if (isInvoiceSelected(invoice)) {
            setselectedRow(selectedRow.filter((item) => item._id !== invoice._id));
        } else {
            setselectedRow([...selectedRow, invoice]);
        }
    };

    const handleSelectAll = () => {
        if (selectedRow.length === filteredBrands.length && filteredBrands.length > 0) {
            setselectedRow([]);
        } else {
            setselectedRow([...filteredBrands]);
        }
    };

    const isAllSelected = () => {
        return filteredBrands.length > 0 && selectedRow.length === filteredBrands.length;
    };

    const isSomeSelected = () => {
        return selectedRow.length > 0 && selectedRow.length < filteredBrands.length;
    };

    function capitalizeFirstWord(str) {
        str = str.toLowerCase();
        let words = str.split(" ");
        if (words.length > 0) {
            words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
        }
        return words.join(" ");
    }

    const renderInvoiceCard = (invoice) => {
        const isSelected = isInvoiceSelected(invoice);

        return (
            <div key={invoice._id} className={`${styles.invoiceCard} ${isSelected ? styles.selected : ""}`}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardCheckbox} onClick={(e) => toggleInvoiceSelection(invoice, e)}>
                        <input type="checkbox" checked={isSelected} onChange={(e) => toggleInvoiceSelection(invoice, e)} onClick={(e) => e.stopPropagation()} />
                        <span className={styles.invoiceId}>#{invoice.invoiceId}</span>
                    </div>
                    <button
                        className={styles.cardAction}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleActionButton(invoice);
                        }}
                    >
                        <i className="pi pi-eye" />
                    </button>
                </div>

                <div className={styles.cardBody}>
                    <div className={styles.cardRow}>
                        <div className={styles.cardField}>
                            <i className="pi pi-user" />
                            <div>
                                <span className={styles.fieldLabel}>Client</span>
                                <span className={styles.fieldValue}>{invoice.clientName}</span>
                            </div>
                        </div>
                        <div className={styles.cardField}>
                            <i className="pi pi-tag" />
                            <div>
                                <span className={styles.fieldLabel}>Type</span>
                                <span className={styles.fieldValue}>{invoice.customerType}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.cardRow}>
                        <div className={styles.cardField}>
                            <i className="pi pi-send" />
                            <div>
                                <span className={styles.fieldLabel}>Shipment</span>
                                <span className={styles.fieldValue}>{invoice.shipmentNumber || (invoice.invoiceCategory === "purchase" ? "Item Purchase" : "N/A")}</span>
                            </div>
                        </div>
                        <div className={styles.cardField}>
                            <i className="pi pi-box" />
                            <div>
                                <span className={styles.fieldLabel}>Weight</span>
                                <span className={styles.fieldValue}>{invoice.weight?.toFixed(2)} lb</span>
                            </div>
                        </div>
                    </div>

                    {invoice.descriptions && (
                        <div className={styles.cardDescription}>
                            <i className="pi pi-info-circle" />
                            <span>{invoice.descriptions}</span>
                        </div>
                    )}

                    <div className={styles.cardAmounts}>
                        <div className={styles.amountItem}>
                            <span className={styles.amountLabel}>Invoice Amount</span>
                            <span className={styles.amountValue}>${invoice.invoiceAmount?.toFixed(2)}</span>
                        </div>
                        {invoice.paidAmount > 0 && (
                            <div className={styles.amountItem}>
                                <span className={styles.amountLabel}>Paid</span>
                                <span className={`${styles.amountValue} ${styles.paid}`}>${invoice.paidAmount?.toFixed(2)}</span>
                            </div>
                        )}
                    </div>

                    <div className={styles.cardFooter}>
                        <div className={styles.footerItem}>
                            <i className="pi pi-calendar" />
                            <span>{invoice.date}</span>
                        </div>
                        {activeTab === "paid" && invoice.datePaid && (
                            <div className={styles.footerItem}>
                                <i className="pi pi-check-circle" />
                                <span>Paid: {invoice.datePaid}</span>
                            </div>
                        )}
                        {invoice.invoiceType && <div className={`${styles.invoiceTypeBadge} ${styles[invoice.invoiceType?.toLowerCase()]}`}>{invoice.invoiceType}</div>}
                    </div>
                </div>
            </div>
        );
    };

    const renderSearchBar = () => {
        return (
            <div className={styles.searchContainer}>
                <div className={styles.globalSearchBar}>
                    <i className="pi pi-search" />
                    <input type="text" placeholder={`Search ${activeTab} invoices by Invoice ID, Shipment Number, Contents, etc...`} value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} className={styles.globalSearchInput} />
                    {globalSearch && (
                        <button className={styles.clearSearchBtn} onClick={() => setGlobalSearch("")}>
                            <i className="pi pi-times" />
                        </button>
                    )}
                </div>

                <div className={styles.searchActions}>
                    <button className={styles.advancedSearchToggle} onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}>
                        <i className={`pi ${showAdvancedSearch ? "pi-filter-slash" : "pi-filter"}`} />
                        {showAdvancedSearch ? "Hide Filters" : "Advanced Filters"}
                    </button>

                    {(globalSearch || Object.values(advancedFilters).some((v) => v)) && (
                        <button className={styles.clearAllBtn} onClick={handleClearFilters}>
                            <i className="pi pi-times-circle" />
                            Clear All
                        </button>
                    )}

                    <div className={styles.resultCount}>
                        <i className="pi pi-info-circle" />
                        <span>
                            Showing {filteredBrands.length} of {brands.length} invoices
                        </span>
                    </div>
                </div>

                {isFilterActive() && filteredBrands.length > 0 && (
                    <div className={styles.selectAllContainer}>
                        <div className={styles.selectAllCheckbox} onClick={handleSelectAll}>
                            <input
                                type="checkbox"
                                checked={isAllSelected()}
                                ref={(input) => {
                                    if (input) {
                                        input.indeterminate = isSomeSelected();
                                    }
                                }}
                                onChange={handleSelectAll}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span>{isAllSelected() ? `Deselect all ${filteredBrands.length} filtered invoices` : isSomeSelected() ? `${selectedRow.length} selected - Select all ${filteredBrands.length} filtered invoices` : `Select all ${filteredBrands.length} filtered invoices`}</span>
                        </div>
                    </div>
                )}

                {showAdvancedSearch && (
                    <div className={styles.advancedFilters}>
                        <h4>
                            <i className="pi pi-sliders-h" />
                            Advanced Search Filters
                        </h4>
                        <div className={styles.filterGrid}>
                            <div className={styles.filterField}>
                                <label>Invoice ID</label>
                                <input type="text" placeholder="Search by Invoice ID..." value={advancedFilters.invoiceId} onChange={(e) => setAdvancedFilters({ ...advancedFilters, invoiceId: e.target.value })} />
                            </div>

                            {activeTab === "paid" && (
                                <div className={styles.filterField}>
                                    <label>Payment ID</label>
                                    <input type="text" placeholder="Search by Payment ID..." value={advancedFilters.invoiceNo} onChange={(e) => setAdvancedFilters({ ...advancedFilters, invoiceNo: e.target.value })} />
                                </div>
                            )}

                            <div className={styles.filterField}>
                                <label>Shipment Number</label>
                                <input type="text" placeholder="Search by Shipment Number..." value={advancedFilters.shipmentNumber} onChange={(e) => setAdvancedFilters({ ...advancedFilters, shipmentNumber: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Contents/Description</label>
                                <input type="text" placeholder="Search by Contents..." value={advancedFilters.descriptions} onChange={(e) => setAdvancedFilters({ ...advancedFilters, descriptions: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Invoice Type</label>
                                <input type="text" placeholder="Search by Invoice Type..." value={advancedFilters.invoiceType} onChange={(e) => setAdvancedFilters({ ...advancedFilters, invoiceType: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Issue Date</label>
                                <input type="text" placeholder="Search by Issue Date..." value={advancedFilters.date} onChange={(e) => setAdvancedFilters({ ...advancedFilters, date: e.target.value })} />
                            </div>

                            {activeTab === "paid" && (
                                <div className={styles.filterField}>
                                    <label>Payment Date</label>
                                    <input type="text" placeholder="Search by Payment Date..." value={advancedFilters.datePaid} onChange={(e) => setAdvancedFilters({ ...advancedFilters, datePaid: e.target.value })} />
                                </div>
                            )}

                            <div className={styles.filterField}>
                                <label>Weight</label>
                                <input type="text" placeholder="Search by Weight..." value={advancedFilters.weight} onChange={(e) => setAdvancedFilters({ ...advancedFilters, weight: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Invoice Amount</label>
                                <input type="text" placeholder="Search by Amount..." value={advancedFilters.invoiceAmount} onChange={(e) => setAdvancedFilters({ ...advancedFilters, invoiceAmount: e.target.value })} />
                            </div>

                            {activeTab === "paid" && (
                                <div className={styles.filterField}>
                                    <label>Paid Amount</label>
                                    <input type="text" placeholder="Search by Paid Amount..." value={advancedFilters.paidAmount} onChange={(e) => setAdvancedFilters({ ...advancedFilters, paidAmount: e.target.value })} />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const onPageChange = (event) => {
        setCurrentPage(event.page);
    };

    const renderInvoices = () => {
        if (activeTab === "paid") {
            if (paginatedGroups.length === 0) {
                return null;
            }
            return paginatedGroups.map(([date, invoices]) => (
                <div key={date} className={styles.groupContainer}>
                    <div className={styles.dateBanner}>
                        <i className="pi pi-calendar" />
                        <span>Payment Date: {date}</span>
                        <span className={styles.count}>({invoices.length} invoices)</span>
                    </div>
                    <div className={styles.cardsGrid}>{invoices.map((invoice) => renderInvoiceCard(invoice))}</div>
                </div>
            ));
        }

        if (paginatedInvoices.length === 0) {
            return null;
        }
        return <div className={styles.cardsGrid}>{paginatedInvoices.map((invoice) => renderInvoiceCard(invoice))}</div>;
    };

    const totalGroups = Object.keys(groupedData).length;
    const paginationTotal = activeTab === "paid" ? totalGroups : filteredBrands.length;
    const paginationPageSize = activeTab === "paid" ? groupsPerPage : invoicesPerPage;
    const selectedTotal = selectedRow.reduce((sum, item) => sum + (item.invoiceAmount || 0), 0);

    return (
        <div className={styles.pageContainer}>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>{capitalizeFirstWord(activeTab)} Invoices</h1>
                    <div className={styles.statsBar}>
                        <div className={styles.statItem}>
                            <i className="pi pi-file" />
                            <span>{brands.length} Total Invoices</span>
                        </div>
                        {selectedRow.length > 0 && (
                            <div className={styles.statItem}>
                                <i className="pi pi-check-square" />
                                <span>{selectedRow.length} Selected</span>
                            </div>
                        )}
                        {activeTab === "paid" && (
                            <div className={styles.statItem}>
                                <i className="pi pi-box" />
                                <span>{totalGroups} Groups</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.actionBar}>
                    <Tooltip title={selectedRow.length ? "Pay Selected Invoices" : "Select invoices to pay"} position="bottom" trigger="mouseenter" arrow={true}>
                        <Button icon="pi pi-wallet" label={selectedRow.length > 0 ? `Pay $${selectedTotal.toFixed(2)}` : "Pay"} onClick={handleSelected} className={styles.btnPrimary} disabled={selectedRow.length === 0} />
                    </Tooltip>
                </div>
            </div>

            <div className={styles.navigationButtons}>
                {activeTab === "unpaid" ? (
                    <button className={`${styles.navButton} ${styles.green}`} onClick={() => switchTab("paid")}>
                        Paid Invoices
                    </button>
                ) : (
                    <button className={`${styles.navButton} ${styles.blue}`} onClick={() => switchTab("unpaid")}>
                        Unpaid Invoices
                    </button>
                )}
            </div>

            {renderSearchBar()}

            <div className={styles.contentArea}>
                {loading ? (
                    <div className={styles.loadingState}>
                        <i className="pi pi-spinner pi-spin" style={{ fontSize: "3rem" }} />
                        <p>Loading invoices...</p>
                    </div>
                ) : filteredBrands.length === 0 ? (
                    <div className={styles.emptyState}>
                        <i className="pi pi-inbox" style={{ fontSize: "4rem" }} />
                        <h3>No invoices found</h3>
                        <p>{globalSearch || Object.values(advancedFilters).some((v) => v) ? "No invoices match your search criteria. Try adjusting your filters." : `There are no ${activeTab} invoices at the moment.`}</p>
                        {(globalSearch || Object.values(advancedFilters).some((v) => v)) && (
                            <button className={styles.clearFiltersBtn} onClick={handleClearFilters}>
                                <i className="pi pi-filter-slash" />
                                Clear All Filters
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {renderInvoices()}

                        {!isFilterActive() && paginationTotal > paginationPageSize && (
                            <div className={styles.paginationContainer}>
                                <Paginator
                                    first={currentPage * paginationPageSize}
                                    rows={paginationPageSize}
                                    totalRecords={paginationTotal}
                                    onPageChange={onPageChange}
                                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                    currentPageReportTemplate={activeTab === "paid" ? "Showing {first} to {last} of {totalRecords} groups" : "Showing {first} to {last} of {totalRecords} invoices"}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default BillingReports;
